import type React from "react";
import { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import type { ToolCallEntry } from "@/api/ai/types";
import { OverlayScroll } from "@/components/OverlayScroll";

interface ToolCallBadgeProps {
  entry: ToolCallEntry;
}

function badgeRowProps(
  canExpand: boolean,
  expanded: boolean,
  toggle: () => void,
) {
  if (!canExpand) return { as: "div" as const, $clickable: false };
  return {
    as: "button" as const,
    type: "button" as const,
    onClick: toggle,
    "aria-expanded": expanded,
    $clickable: true,
  };
}

export const ToolCallBadge: React.FC<ToolCallBadgeProps> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const hasResult = entry.result != null && entry.result.length > 0;
  const canExpand = hasResult && entry.status !== "executing";
  const toggle = () => setExpanded((v) => !v);

  return (
    <Container>
      <BadgeRow {...badgeRowProps(canExpand, expanded, toggle)}>
        <StatusIcon $status={entry.status} aria-label={entry.status} />
        <ToolName>{entry.name}</ToolName>
        {canExpand && <ExpandHint>{expanded ? "▾" : "▸"}</ExpandHint>}
      </BadgeRow>
      {expanded && hasResult && (
        <ResultScroll>
          <ResultPanel>{entry.result}</ResultPanel>
        </ResultScroll>
      )}
    </Container>
  );
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  font-size: 11px;
`;

const BadgeRow = styled.div<{ $clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: none;
  border-radius: 4px;
  background: ${(p) => p.theme.tableTheadCellBackground};
  font: inherit;
  color: inherit;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  user-select: none;

  &:hover {
    background: ${(p) =>
      p.$clickable
        ? p.theme.cards2Background
        : p.theme.tableTheadCellBackground};
  }
`;

const StatusIcon = styled.span<{ $status: ToolCallEntry["status"] }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;

  ${(p) => {
    switch (p.$status) {
      case "executing":
        return css`
          border: 2px solid ${p.theme.badgeWarningSubtleColor ?? "#e5a100"};
          border-top-color: transparent;
          animation: ${spin} 0.6s linear infinite;
        `;
      case "complete":
        return css`
          background: ${p.theme.badgeConfirmSubtleColor ?? "#00a650"};
        `;
      case "error":
        return css`
          background: ${p.theme.badgeErrorSubtleColor ?? "#d32f2f"};
        `;
      case "denied":
        return css`
          background: ${p.theme.semanticTextPlaceholder ?? "#999"};
        `;
      case "pending":
        return css`
          border: 2px solid ${p.theme.semanticTextPlaceholder ?? "#999"};
          border-top-color: transparent;
          animation: ${spin} 0.8s linear infinite;
        `;
      default:
        return css`
          background: ${p.theme.semanticTextPlaceholder ?? "#999"};
        `;
    }
  }}
`;

const ToolName = styled.span`
  color: ${(p) => p.theme.semanticTextBody};
  font-weight: 500;
`;

const ExpandHint = styled.span`
  color: ${(p) => p.theme.semanticTextPlaceholder};
  font-size: 10px;
`;

// The result box scrolls past its cap on the app's scrollbars; the frame
// (background, border) sits on the scroll host so the bar runs inside it.
const ResultScroll = styled(OverlayScroll)`
  margin: 4px 0 0 0;
  max-height: 200px;
  background: ${(p) => p.theme.editorBackground};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
`;

const ResultPanel = styled.pre`
  margin: 0;
  padding: 8px 10px;
  font-family: "JetBrains Mono", "Fira Code", "Monaco", "Consolas", monospace;
  font-size: 11px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${(p) => p.theme.editorText};
`;

export default ToolCallBadge;
